import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, X, Send, Loader2, Sparkles, Crown, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/services/api';
import { Link } from 'react-router-dom';

// Types
interface ChatMessage {
    id?: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    created_at?: string;
    isTemporary?: boolean;
}

// Chat Panel Component
function ChatPanel({
    isOpen,
    onClose,
    workspaceId,
}: {
    isOpen: boolean;
    onClose: () => void;
    workspaceId: string;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Fetch chat history
    const { data: historyData, isLoading: isLoadingHistory } = useQuery({
        queryKey: ['chat-history', workspaceId],
        queryFn: () => api.getChatHistory(workspaceId),
        enabled: isOpen && !!workspaceId,
    });

    // Update messages when history loads
    useEffect(() => {
        if (historyData?.messages) {
            setMessages(historyData.messages.map((m: any) => ({
                id: m.id,
                role: m.role,
                content: m.content,
                created_at: m.created_at,
            })));
        }
    }, [historyData]);

    // Send message mutation
    const sendMutation = useMutation({
        mutationFn: (message: string) => api.sendChatMessage(workspaceId, message),
        onMutate: (message) => {
            // Optimistically add user message
            const userMessage: ChatMessage = {
                role: 'user',
                content: message,
                isTemporary: true,
            };
            setMessages(prev => [...prev, userMessage]);
            setInput('');
        },
        onSuccess: (data) => {
            // Add AI response
            const assistantMessage: ChatMessage = {
                role: 'assistant',
                content: data.response,
            };
            setMessages(prev => {
                // Remove temporary flag from user message and add AI response
                const userMessage = prev.find(m => m.isTemporary);
                if (!userMessage) return prev; // Shouldn't happen

                const filtered = prev.filter(m => !m.isTemporary);
                return [...filtered, { ...userMessage, isTemporary: false }, assistantMessage];
            });
            queryClient.invalidateQueries({ queryKey: ['chat-history', workspaceId] });
        },
        onError: (error: any) => {
            console.error('AI Chat error:', error);
            // Remove temporary message and show error
            setMessages(prev => {
                const filtered = prev.filter(m => !m.isTemporary);
                return [...filtered, {
                    role: 'assistant',
                    content: `❌ Lỗi: ${error?.message || 'Không thể kết nối với AI. Vui lòng thử lại.'}`,
                    isTemporary: false,
                }];
            });
        },
    });

    // Clear history mutation
    const clearMutation = useMutation({
        mutationFn: () => api.clearChatHistory(workspaceId),
        onSuccess: () => {
            setMessages([]);
            queryClient.invalidateQueries({ queryKey: ['chat-history', workspaceId] });
        },
    });

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input when panel opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    const handleSend = () => {
        const trimmed = input.trim();
        if (!trimmed || sendMutation.isPending) return;
        sendMutation.mutate(trimmed);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed bottom-20 right-4 w-96 max-w-[calc(100vw-2rem)] h-[500px] max-h-[70vh] bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-700 flex flex-col z-50 animate-scale-in">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-700 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-t-2xl">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    <div>
                        <h3 className="font-semibold text-sm">{t('aiChat.title', 'Trợ lý VisionBoard')}</h3>
                        <p className="text-xs text-white/80">{t('aiChat.subtitle', 'Hỏi đáp về công việc và mục tiêu')}</p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {messages.length > 0 && (
                        <button
                            onClick={() => clearMutation.mutate()}
                            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                            title={t('common.delete', 'Xóa')}
                            disabled={clearMutation.isPending}
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {isLoadingHistory ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="text-center text-neutral-500 dark:text-neutral-400 py-8">
                        <Sparkles className="w-12 h-12 mx-auto mb-3 text-primary-400" />
                        <p className="text-sm whitespace-pre-line">
                            {t('aiChat.welcome', 'Xin chào! Tôi là trợ lý AI của VisionBoard. Tôi có thể giúp bạn:\n• Tổng hợp tiến độ mục tiêu\n• Đề xuất ưu tiên công việc\n• Trả lời câu hỏi về OKR và Kanban')}
                        </p>
                    </div>
                ) : (
                    <>
                        {messages.map((msg, idx) => (
                            <div
                                key={msg.id || idx}
                                className={clsx(
                                    'flex',
                                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                                )}
                            >
                                <div
                                    className={clsx(
                                        'max-w-[85%] rounded-2xl px-4 py-2.5 text-sm',
                                        msg.role === 'user'
                                            ? 'bg-primary-500 text-white rounded-br-md'
                                            : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200 rounded-bl-md'
                                    )}
                                >
                                    <p className="whitespace-pre-wrap">{msg.content}</p>
                                </div>
                            </div>
                        ))}
                        {sendMutation.isPending && (
                            <div className="flex justify-start">
                                <div className="bg-neutral-100 dark:bg-neutral-700 rounded-2xl rounded-bl-md px-4 py-3">
                                    <div className="flex items-center gap-2 text-sm text-neutral-500">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {t('aiChat.thinking', 'Đang suy nghĩ...')}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-neutral-200 dark:border-neutral-700">
                <div className="flex items-end gap-2">
                    <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t('aiChat.placeholder', 'Hỏi điều gì đó...')}
                        className="flex-1 resize-none rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 max-h-24"
                        rows={1}
                        disabled={sendMutation.isPending}
                    />
                    <button
                        onClick={handleSend}
                        disabled={!input.trim() || sendMutation.isPending}
                        className={clsx(
                            'p-2.5 rounded-xl transition-colors',
                            input.trim() && !sendMutation.isPending
                                ? 'bg-primary-500 text-white hover:bg-primary-600'
                                : 'bg-neutral-200 dark:bg-neutral-600 text-neutral-400 cursor-not-allowed'
                        )}
                    >
                        <Send className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

// Upgrade Prompt Modal
function UpgradePrompt({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) {
    const { t } = useTranslation();

    if (!isOpen) return null;

    const features = t('aiChat.upgradeRequired.features', {
        returnObjects: true,
        defaultValue: [
            'Hỏi đáp về công việc và mục tiêu',
            'Đề xuất ưu tiên nhiệm vụ',
            'Phân tích tiến độ và đưa ra lời khuyên',
            'Hỗ trợ 24/7'
        ],
    }) as string[];

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-scale-in">
                {/* Header */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
                    <div className="flex items-center gap-3 text-white">
                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                            <Sparkles className="w-7 h-7" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">{t('aiChat.upgradeRequired.title', 'Tính năng PRO')}</h3>
                            <p className="text-white/80 text-sm">AI Assistant</p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6">
                    <p className="text-neutral-600 dark:text-neutral-300 mb-4">
                        {t('aiChat.upgradeRequired.message', 'Trợ lý AI chỉ khả dụng cho gói PRO. Nâng cấp để được tư vấn và hỗ trợ bởi AI.')}
                    </p>

                    <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-xl p-4 mb-6">
                        <p className="font-semibold text-neutral-800 dark:text-neutral-200 mb-2 flex items-center gap-2">
                            <Crown className="w-4 h-4 text-amber-500" />
                            PRO Features
                        </p>
                        <ul className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1.5">
                            {features.map((feature, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                    <span className="text-green-500 mt-0.5">✓</span>
                                    {feature}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-3 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors font-medium"
                        >
                            {t('common.close', 'Đóng')}
                        </button>
                        <Link
                            to="/pricing"
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-colors font-medium flex items-center justify-center gap-2"
                            onClick={onClose}
                        >
                            <Crown className="w-4 h-4" />
                            {t('plan.upgradeToPro', 'Nâng cấp PRO')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Data Consent Modal
function DataConsentModal({
    isOpen,
    onAccept,
    onDecline,
}: {
    isOpen: boolean;
    onAccept: () => void;
    onDecline: () => void;
}) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-scale-in">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-4">
                    <div className="flex items-center gap-3 text-white">
                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                            <Sparkles className="w-7 h-7" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">Đồng ý chia sẻ dữ liệu</h3>
                            <p className="text-white/80 text-sm">VisionBoard AI Assistant</p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                        <p className="font-semibold text-amber-800 dark:text-amber-400 mb-2">
                            ⚠️ Thông báo quan trọng
                        </p>
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                            AI sẽ truy cập dữ liệu workspace của bạn để tư vấn hiệu quả hơn.
                        </p>
                    </div>

                    <div className="space-y-3 text-sm text-neutral-600 dark:text-neutral-300">
                        <p className="font-semibold text-neutral-800 dark:text-neutral-200">
                            AI sẽ có quyền truy cập:
                        </p>
                        <ul className="space-y-2">
                            <li className="flex items-start gap-2">
                                <span className="text-green-500 mt-0.5">✓</span>
                                <span>Mục tiêu (Objectives) và Key Results của workspace hiện tại</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-500 mt-0.5">✓</span>
                                <span>Tasks, boards và tiến độ công việc</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-500 mt-0.5">✓</span>
                                <span>Thống kê và phân tích workspace</span>
                            </li>
                        </ul>

                        <p className="font-semibold text-neutral-800 dark:text-neutral-200 pt-2">
                            Cam kết bảo mật:
                        </p>
                        <ul className="space-y-2">
                            <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">🔒</span>
                                <span>AI CHỈ truy cập dữ liệu workspace bạn có quyền</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">🔒</span>
                                <span>KHÔNG chia sẻ dữ liệu với workspace khác</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">🔒</span>
                                <span>TỪ CHỐI trả lời về dữ liệu người dùng khác</span>
                            </li>
                        </ul>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <button
                            onClick={onDecline}
                            className="flex-1 px-4 py-3 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors font-medium"
                        >
                            Từ chối
                        </button>
                        <button
                            onClick={onAccept}
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-colors font-medium"
                        >
                            Đồng ý
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Main Widget Component
export default function AIChatWidget() {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
    const [showConsentModal, setShowConsentModal] = useState(false);
    const { workspace, user } = useAuthStore();

    // Check if user has PRO plan
    const isPro = (user as any)?.plan === 'pro';

    // Check consent status from localStorage
    const hasConsent = () => {
        return localStorage.getItem('ai_chat_consent') === 'true';
    };

    const handleClick = () => {
        if (!isPro) {
            setShowUpgradePrompt(true);
            return;
        }

        // Check consent first
        if (!hasConsent()) {
            setShowConsentModal(true);
            return;
        }

        setIsOpen(!isOpen);
    };

    const handleConsentAccept = () => {
        localStorage.setItem('ai_chat_consent', 'true');
        setShowConsentModal(false);
        setIsOpen(true);
    };

    const handleConsentDecline = () => {
        setShowConsentModal(false);
    };

    // Don't render if no workspace
    if (!workspace?.id) return null;

    return (
        <>
            {/* Floating Chat Button */}
            <button
                onClick={handleClick}
                className={clsx(
                    'fixed bottom-4 right-4 z-40 w-14 h-14 rounded-full shadow-lg transition-all duration-200',
                    'flex items-center justify-center',
                    'hover:scale-110 active:scale-95',
                    isOpen
                        ? 'bg-neutral-600 text-white'
                        : 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                )}
                title={t('aiChat.title', 'Trợ lý VisionBoard')}
            >
                {isOpen ? (
                    <X className="w-6 h-6" />
                ) : (
                    <>
                        <MessageSquare className="w-6 h-6" />
                        {/* PRO badge for free users */}
                        {!isPro && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                                <Crown className="w-3 h-3 text-white" />
                            </span>
                        )}
                    </>
                )}
            </button>

            {/* Chat Panel (PRO users only) */}
            {isPro && (
                <ChatPanel
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    workspaceId={workspace.id}
                />
            )}

            {/* Upgrade Prompt (FREE users) */}
            <UpgradePrompt
                isOpen={showUpgradePrompt}
                onClose={() => setShowUpgradePrompt(false)}
            />

            {/* Data Consent Modal */}
            <DataConsentModal
                isOpen={showConsentModal}
                onAccept={handleConsentAccept}
                onDecline={handleConsentDecline}
            />
        </>
    );
}
